import { useCallback } from 'react';
import { useDispatch } from 'react-redux';

import { isUnlimited } from '../components/pages/ProfileV2/accountUsage';
import { ANON_MODAL_ENABLED } from '../const';
import { readAnonMessageCounters } from '../service/anonMessageCredits';
import { store } from '../store';
import { getUserId, getUserLimits, getUserPaymentPopupState, setShowAnonModal, toggleAuthModal } from '../store/slices/authSlice';
import {
    attemptToChatWithExceededLimit,
    getAttemptsToChatWithExceededLimit,
    showPaymentModalWithOfferActivation
} from '../store/slices/chatsSlice';
import { getIsRegionRestricted } from '../store/slices/memorySlice';
import { openPaymentModal } from '../store/slices/modalsSlice';
import { gaEvents } from '../utils/analytics';
import { getAnonMessagesUsed, isAnonMessageLimitReached } from '../utils/helper';

// HORO-10455: shared message-send limit gate.
//
// Entry points that create-a-chat-then-navigate (the anonymous homepage via
// useStartHomeChat, and ChatBar's landing-mode first message) previously fired
// sendNewChatMessage without checking the message quota. When the user was out
// of messages the send was silently rejected and the optimistic bubble
// stripped, dropping the user into a blank chat with no paywall / sign-in
// prompt. This hook centralises the same pre-send check ChatBar's normal
// (non-landing) path already does, so those entry points can block at
// submission and show feedback instead.
//
// Returns a callback: call it right before creating the chat / navigating.
// It returns `true` when the send should be aborted (and has already dispatched
// the appropriate modal), or `false` when the caller may proceed.
export const useMessageLimitGate = () => {
    const dispatch = useDispatch();

    return useCallback(() => {
        // Read a fresh snapshot so the gate is correct regardless of how the
        // caller memoises this callback.
        const state = store.getState();
        const userId = getUserId(state);

        if (userId) {
            const limits = getUserLimits(state);
            const { total, used } = limits;
            // `>=` (not `===`) so carry-over overshoot users (used > total)
            // are also gated rather than falling through to a rejected send.
            //
            // `total === null` means the plan is unlimited (HORO-10504) and
            // there is no ceiling to be at. `!== undefined` let null through,
            // and `used >= null` coerces to `used >= 0` — so every unlimited
            // subscriber was gated on their first message and shown a paywall.
            // The other call sites compare `total === used`, which is safely
            // false for null; this one is the only `>=`.
            if (!isUnlimited(limits) && total !== undefined && used !== undefined && used >= total) {
                // Capture attempts BEFORE dispatching the increment so the
                // first-vs-subsequent modal branch matches the pre-increment
                // value (matches ChatBar's selector-captured semantics).
                const attempts = getAttemptsToChatWithExceededLimit(state);
                gaEvents.messageLimitReached(total, true);
                dispatch(attemptToChatWithExceededLimit());
                gaEvents.openPaymentPopup(getUserPaymentPopupState(state));
                if (attempts === 0) {
                    dispatch(openPaymentModal({ source: 'messageLimit' }));
                } else {
                    dispatch(showPaymentModalWithOfferActivation({ source: 'messageLimit' }));
                }
                return true;
            }
            return false;
        }

        const counters = readAnonMessageCounters();
        if (isAnonMessageLimitReached(counters)) {
            gaEvents.signInPrompted(getAnonMessagesUsed(counters));
            if (ANON_MODAL_ENABLED && !getIsRegionRestricted(state)) {
                dispatch(setShowAnonModal(true));
            } else {
                dispatch(toggleAuthModal({ trigger: true, step: 1, context: 'messageLimit' }));
            }
            return true;
        }
        return false;
    }, [dispatch]);
};
